/**
 * ObservationList — compact list of observations for a capture.
 *
 * @remarks
 * Shows description, status badge, confirm and delete actions.
 * "Exported" observations are read-only (no actions shown).
 */

import { OBSERVATION_TYPE_LABELS, type SurveyObservation } from '../types/survey.types';

interface ObservationListProps {
  observations: SurveyObservation[];
  isLoading: boolean;
  markingObsId: string | null;
  onConfirm: (id: string) => void;
  onRemove: (id: string) => void;
  onMark: (id: string) => void;
}

function StatusBadge({ status }: { status: SurveyObservation['status'] }): JSX.Element {
  const labels: Record<SurveyObservation['status'], string> = {
    draft: 'Borrador',
    confirmed: 'Confirmada',
    exported: 'Exportada',
  };
  return <span className={`sv-obs-badge sv-obs-badge--${status}`}>{labels[status]}</span>;
}

// ── MarkingActions — extracted to keep ObservationItem under complexity limit ──

interface MarkingActionsProps {
  obs: SurveyObservation;
  markingObsId: string | null;
  onMark: (id: string) => void;
}

function MarkingActions({ obs, markingObsId, onMark }: MarkingActionsProps): JSX.Element | null {
  if (obs.placementMethod) {
    return <span className="sv-obs-placed-badge">Marcada</span>;
  }
  const isCurrentlyMarking = obs.id === markingObsId;
  const anyMarking = markingObsId !== null;
  const label = isCurrentlyMarking ? 'Click en imagen para marcar' : 'Marcar en imagen';
  const cls = `sv-obs-action-btn sv-obs-action-btn--mark${isCurrentlyMarking ? ' sv-obs-action-btn--mark-active' : ''}`;
  return (
    <button
      type="button"
      className={cls}
      onClick={() => onMark(obs.id)}
      disabled={anyMarking && !isCurrentlyMarking}
      aria-label={label}
    >
      {isCurrentlyMarking ? '…' : '⊙'}
    </button>
  );
}

// ── ObservationItem ───────────────────────────────────────────────────────────

interface ObservationItemProps {
  obs: SurveyObservation;
  markingObsId: string | null;
  onConfirm: (id: string) => void;
  onRemove: (id: string) => void;
  onMark: (id: string) => void;
}

function ObservationItem({
  obs,
  markingObsId,
  onConfirm,
  onRemove,
  onMark,
}: ObservationItemProps): JSX.Element {
  const isExported = obs.status === 'exported';
  const typeLabel = obs.objectType ? OBSERVATION_TYPE_LABELS[obs.objectType] : null;
  return (
    <div className="sv-obs-item">
      <div className="sv-obs-item-header">
        <div className="sv-obs-item-badges">
          <StatusBadge status={obs.status} />
          {typeLabel && <span className="sv-obs-type-badge">{typeLabel}</span>}
        </div>
        {!isExported && (
          <div className="sv-obs-item-actions">
            <MarkingActions obs={obs} markingObsId={markingObsId} onMark={onMark} />
            {obs.status === 'draft' && (
              <button
                type="button"
                className="sv-obs-action-btn sv-obs-action-btn--confirm"
                onClick={() => onConfirm(obs.id)}
                aria-label="Confirmar observación"
              >
                ✓
              </button>
            )}
            <button
              type="button"
              className="sv-obs-action-btn sv-obs-action-btn--remove"
              onClick={() => onRemove(obs.id)}
              aria-label="Eliminar observación"
            >
              ✕
            </button>
          </div>
        )}
      </div>
      <p className="sv-obs-description">{obs.description}</p>
    </div>
  );
}

export function ObservationList({
  observations,
  isLoading,
  markingObsId,
  onConfirm,
  onRemove,
  onMark,
}: ObservationListProps): JSX.Element {
  if (isLoading) {
    return <p className="sv-obs-state">Cargando…</p>;
  }
  if (observations.length === 0) {
    return <p className="sv-obs-state">Sin observaciones.</p>;
  }
  return (
    <div className="sv-obs-list">
      {observations.map(obs => (
        <ObservationItem
          key={obs.id}
          obs={obs}
          markingObsId={markingObsId}
          onConfirm={onConfirm}
          onRemove={onRemove}
          onMark={onMark}
        />
      ))}
    </div>
  );
}
